import { cn } from '@/lib/utils';

import SourceTypeIcon from '@/components/icons/SourceTypeIcon';

import { isKnownSourceType } from '@/types/sourceTypeRegistry';

import type { SourceSection } from './SourceSummaryContext';

export function SourceTypeIconBox({ configType }: { configType?: string }) {
    const isMcpFallback = !isKnownSourceType(configType);
    return (
        <div
            className={cn(
                'flex justify-center items-center border rounded-sm w-8 h-8 shrink-0',
                isMcpFallback
                    ? 'bg-black border-black'
                    : 'bg-white border-border'
            )}
        >
            <SourceTypeIcon
                configType={configType}
                className={cn('size-5', isMcpFallback && 'text-white')}
            />
        </div>
    );
}

export function capitalizeFirstLetter(value: string) {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
}

export function formatSourceDisplayName(name: string): string {
    if (!name) return name;
    return name
        .replace(/[_-]+/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export function matchSection(
    sections: SourceSection[],
    name: string
): SourceSection | null {
    const norm = name.toLowerCase();
    return (
        sections.find((s) => {
            const sn = s.name.toLowerCase();
            return sn === norm || sn.startsWith(norm) || norm.startsWith(sn);
        }) ?? null
    );
}

function ParamGrid({ call }: { call: SourceSection['calls'][number] }) {
    const items = [
        { label: 'Request', value: call.request },
        ...call.params
            .filter((p) => p.key || p.value)
            .map((p) => ({ label: p.key || '—', value: p.value })),
        ...(call.pages && call.pages.length > 0
            ? [{ label: 'Sider', value: call.pages.join(', ') }]
            : [])
    ];
    if (items.length === 0) return null;

    const useSingleColumn = items.length <= 2;
    const mid = Math.ceil(items.length / 2);
    const cols = useSingleColumn
        ? [items]
        : [items.slice(0, mid), items.slice(mid)].filter((c) => c.length > 0);

    return (
        <div
            className={cn(
                'box-border flex items-start bg-muted/50 p-3.5 rounded-md w-full',
                useSingleColumn ? 'gap-0' : 'gap-9'
            )}
        >
            {cols.map((col, ci) => (
                <div key={ci} className="flex flex-col flex-1 gap-4 min-w-0">
                    {col.map((item, i) => (
                        <div key={i} className="flex flex-col gap-1.5">
                            <span className="font-medium text-foreground text-sm wrap-break-word leading-5 whitespace-normal">
                                {capitalizeFirstLetter(item.label)}
                            </span>
                            <span className="font-normal text-muted-foreground text-sm wrap-break-word leading-5 whitespace-normal">
                                {item.value}
                            </span>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

export function SourceCallGrid({ section }: { section: SourceSection }) {
    if (section.calls.length === 0) return null;
    return (
        <>
            {section.calls.map((call, i) => (
                <ParamGrid key={i} call={call} />
            ))}
        </>
    );
}
